import React, { useEffect, useMemo, useState } from "react";
import { http, formatError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Mail, Send, Eye, Save, RefreshCw, Smartphone, Monitor, Search } from "lucide-react";
import { toast } from "sonner";

const AUDIENCE_STYLES = {
  customer: "bg-primary/5 text-primary border border-primary/20",
  student: "bg-secondary/10 text-secondary border border-secondary/20",
  cleaner: "bg-amber-50 text-amber-700 border border-amber-200",
  admin: "bg-slate-100 text-slate-700 border border-slate-300",
};

export default function EmailCenter() {
  const [templates, setTemplates] = useState([]);
  const [status, setStatus] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [q, setQ] = useState("");

  async function loadAll() {
    const [t, s, d] = await Promise.all([
      http.get("/emails/templates"), http.get("/emails/status"), http.get("/emails/deliveries?page_size=50"),
    ]);
    setTemplates(t.data); setStatus(s.data); setDeliveries(d.data);
  }
  useEffect(() => { loadAll(); }, []);

  const filtered = useMemo(() =>
    templates.filter(t => !q || t.code.toLowerCase().includes(q.toLowerCase()) || (t.name || "").toLowerCase().includes(q.toLowerCase())),
    [templates, q]);

  return (
    <div className="space-y-5" data-testid="email-center">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Communications</div>
          <h2 className="font-display text-2xl font-bold text-primary">Email Center</h2>
        </div>
        {status && (
          <div className="flex items-center gap-2">
            <Badge className={status.mode === "live" ? "bg-secondary/10 text-secondary border border-secondary/20" : "bg-amber-50 text-amber-700 border border-amber-200"}>
              <Mail size={11} className="mr-1" /> {status.mode === "live" ? "Live via Resend" : "Mock mode — no real sends"}
            </Badge>
            <Badge variant="outline">{status.counts.templates} templates</Badge>
            <Badge variant="outline">{status.counts.mocked + status.counts.sent} sent</Badge>
            {status.counts.failed > 0 && <Badge className="bg-red-50 text-red-700 border border-red-200">{status.counts.failed} failed</Badge>}
          </div>
        )}
      </div>

      <Tabs defaultValue="templates">
        <TabsList>
          <TabsTrigger value="templates" data-testid="email-tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="deliveries" data-testid="email-tab-deliveries">Delivery log</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="mt-4">
          <div className="relative mb-3">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search templates…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="email-search" />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {filtered.map((t) => (
              <TemplateCard key={t.code} tpl={t} onChanged={loadAll} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="deliveries" className="mt-4">
          <DeliveryLog deliveries={deliveries} onReload={loadAll} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TemplateCard({ tpl, onChanged }) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="border-border/60"><CardContent className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display font-semibold text-primary truncate">{tpl.name}</div>
          <div className="text-[11px] font-mono text-muted-foreground truncate">{tpl.code}</div>
          <div className="text-xs text-muted-foreground mt-1 truncate italic">{tpl.subject}</div>
        </div>
        <Badge className={AUDIENCE_STYLES[tpl.audience] || "bg-muted"}>{tpl.audience}</Badge>
      </div>
      <div className="mt-3 flex gap-2">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" data-testid={`edit-tpl-${tpl.code}`}>Open</Button>
          </DialogTrigger>
          <TemplateEditor code={tpl.code} open={open} onClose={() => { setOpen(false); onChanged(); }} />
        </Dialog>
      </div>
    </CardContent></Card>
  );
}

function TemplateEditor({ code, open, onClose }) {
  const [tpl, setTpl] = useState(null);
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [preview, setPreview] = useState(null);
  const [device, setDevice] = useState("desktop");
  const [testTo, setTestTo] = useState("");

  useEffect(() => {
    if (!open) return;
    http.get(`/emails/templates/${code}`).then(({ data }) => {
      setTpl(data); setSubject(data.subject); setHtml(data.html);
    });
  }, [open, code]);

  async function doPreview() {
    try {
      const { data } = await http.post("/emails/preview", { subject, html });
      setPreview(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  }

  async function save() {
    try {
      await http.patch(`/emails/templates/${code}`, { subject, html });
      toast.success("Saved. Live for future sends.");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  }

  async function sendTest() {
    if (!testTo) { toast.error("Enter a test email address"); return; }
    try {
      const { data } = await http.post("/emails/test-send", { template_code: code, to: testTo });
      if (data.ok) toast.success(data.mocked ? "Test send logged (mock mode)" : `Sent — ${data.email_id || "id pending"}`);
      else toast.error(data.error || "Send failed");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  }

  if (!tpl) return <DialogContent><div className="p-6">Loading…</div></DialogContent>;
  return (
    <DialogContent className="max-w-6xl max-h-[92vh] overflow-hidden flex flex-col p-0">
      <DialogHeader className="p-5 border-b"><DialogTitle>{tpl.name} <span className="text-xs font-mono text-muted-foreground ml-2">{code}</span></DialogTitle></DialogHeader>
      <div className="grid md:grid-cols-2 gap-0 flex-1 overflow-hidden">
        {/* Editor */}
        <div className="p-5 border-r overflow-y-auto space-y-3">
          <div>
            <Label className="text-xs">Subject line</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="edit-subject" />
          </div>
          <div>
            <Label className="text-xs">HTML body (Jinja2 variables: {`{{ variable }}`})</Label>
            <Textarea rows={20} value={html} onChange={(e) => setHtml(e.target.value)} className="font-mono text-xs" data-testid="edit-html" />
          </div>
          <div className="flex gap-2">
            <Button onClick={doPreview} variant="outline" data-testid="preview-btn"><Eye size={14} className="mr-1" /> Preview</Button>
            <Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="save-btn"><Save size={14} className="mr-1" /> Save</Button>
          </div>
          <div className="border-t pt-3 space-y-2">
            <Label className="text-xs">Send test to</Label>
            <div className="flex gap-2">
              <Input type="email" placeholder="you@example.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="test-to" />
              <Button onClick={sendTest} className="bg-secondary hover:bg-secondary/90" data-testid="test-send-btn"><Send size={14} className="mr-1" /> Send</Button>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="p-5 overflow-y-auto bg-muted/30">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Live preview</div>
            <div className="flex gap-1">
              <Button size="sm" variant={device === "desktop" ? "default" : "ghost"} onClick={() => setDevice("desktop")} data-testid="preview-desktop"><Monitor size={13} /></Button>
              <Button size="sm" variant={device === "mobile" ? "default" : "ghost"} onClick={() => setDevice("mobile")} data-testid="preview-mobile"><Smartphone size={13} /></Button>
            </div>
          </div>
          {preview ? (
            <div className={`bg-background rounded-lg border shadow overflow-hidden mx-auto ${device === "mobile" ? "max-w-[360px]" : ""}`}>
              <div className="p-2 border-b bg-muted/40 text-xs font-semibold text-primary">📨 {preview.subject}</div>
              <iframe title="preview" srcDoc={preview.html} className="w-full" style={{ height: 700, border: 0 }} />
            </div>
          ) : (
            <div className="text-center text-sm text-muted-foreground py-16">Click <b>Preview</b> to render the email with sample data.</div>
          )}
        </div>
      </div>
    </DialogContent>
  );
}

function DeliveryLog({ deliveries, onReload }) {
  const [viewing, setViewing] = useState(null);

  async function resend(id) {
    try {
      await http.post(`/emails/deliveries/${id}/resend`);
      toast.success("Resent");
      onReload();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  }

  async function openDelivery(id) {
    const { data } = await http.get(`/emails/deliveries/${id}`);
    setViewing(data);
  }

  return (
    <>
      <Card className="border-border/60"><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left p-3">When</th>
              <th className="text-left p-3">Template</th>
              <th className="text-left p-3">To</th>
              <th className="text-left p-3">Subject</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {deliveries.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-muted-foreground">No deliveries yet.</td></tr>}
            {deliveries.map((d) => (
              <tr key={d.id} className="border-t border-border/60">
                <td className="p-3 text-xs text-muted-foreground">{d.created_at?.slice(0, 16).replace("T", " ")}</td>
                <td className="p-3 font-mono text-xs">{d.template_code}</td>
                <td className="p-3">{d.to}</td>
                <td className="p-3 text-muted-foreground truncate max-w-xs">{d.subject}</td>
                <td className="p-3">
                  <Badge className={
                    d.status === "sent" ? "bg-secondary/10 text-secondary border border-secondary/20" :
                    d.status === "mocked" ? "bg-amber-50 text-amber-700 border border-amber-200" :
                    "bg-red-50 text-red-700 border border-red-200"
                  }>{d.status}</Badge>
                </td>
                <td className="p-3 text-right space-x-1">
                  {d.has_preview && <Button size="sm" variant="ghost" onClick={() => openDelivery(d.id)}><Eye size={12} /></Button>}
                  <Button size="sm" variant="outline" onClick={() => resend(d.id)}><RefreshCw size={12} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>

      {viewing && (
        <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
          <DialogContent className="max-w-3xl max-h-[85vh] p-0 flex flex-col">
            <DialogHeader className="p-4 border-b"><DialogTitle>{viewing.subject}</DialogTitle></DialogHeader>
            <iframe title="delivery-preview" srcDoc={viewing.preview_html || "<p>No preview stored</p>"} className="w-full flex-1 border-0" style={{ minHeight: 600 }} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
